import { useState } from 'react';
import {
  DollarSign,
  TrendingUp,
  Users,
  Calendar,
  CreditCard,
  Target,
  Settings
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell
} from 'recharts';

interface OperationalInsightsProps {
  monthlyRevenue: number;
  performance: any;
  organization: any;
}

export function OperationalInsights({ monthlyRevenue, performance, organization: _organization }: OperationalInsightsProps) {
  const [_selectedPeriod, _setSelectedPeriod] = useState('month');

  // Mock subscription and billing data
  const subscriptionData = {
    currentPlan: 'ACADEMY',
    planLimits: {
      maxPlayers: 200,
      maxCoaches: 15,
      maxSessions: 1000,
      storageGB: 100
    },
    currentUsage: {
      players: 156,
      coaches: 12,
      sessions: 308,
      storageGB: 45
    },
    billing: {
      monthlyAmount: 299,
      nextBillingDate: '2024-09-18',
      paymentMethod: '**** **** **** 4532',
      status: 'active'
    }
  };

  // Mock revenue data
  const revenueData = [
    { month: 'Jan', revenue: 38000, expenses: 25000, profit: 13000, students: 142 },
    { month: 'Feb', revenue: 41000, expenses: 26500, profit: 14500, students: 148 },
    { month: 'Mar', revenue: 43500, expenses: 27200, profit: 16300, students: 151 },
    { month: 'Apr', revenue: 44800, expenses: 28100, profit: 16700, students: 155 },
    { month: 'May', revenue: 45200, expenses: 28800, profit: 16400, students: 156 },
    { month: 'Jun', revenue: 47100, expenses: 29200, profit: 17900, students: 163 }
  ];

  // Mock growth metrics
  const growthMetrics = [
    { metric: 'Student Enrollment', current: 156, target: 180, growth: 12.5 },
    { metric: 'Monthly Revenue', current: 47100, target: 50000, growth: 8.7 },
    { metric: 'Coach Utilization', current: 78, target: 85, growth: 5.2 },
    { metric: 'Session Volume', current: 308, target: 350, growth: 15.3 }
  ];

  // Mock retention analytics
  const retentionData = [
    { period: '1 Month', retained: 98, churned: 2 },
    { period: '3 Months', retained: 94, churned: 6 },
    { period: '6 Months', retained: 89, churned: 11 },
    { period: '12 Months', retained: 85, churned: 15 }
  ];

  // Mock expense breakdown
  const expenseBreakdown = [
    { category: 'Staff Salaries', amount: 18500, percentage: 63.4, color: '#3B82F6' },
    { category: 'Facility Costs', amount: 5200, percentage: 17.8, color: '#10B981' },
    { category: 'Equipment', amount: 2800, percentage: 9.6, color: '#F59E0B' },
    { category: 'Marketing', amount: 1500, percentage: 5.1, color: '#8B5CF6' },
    { category: 'Technology', amount: 1200, percentage: 4.1, color: '#EF4444' }
  ];

  const getUsagePercentage = (current: number, max: number) => {
    return Math.round((current / max) * 100);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Financial Overview Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(monthlyRevenue)}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">+8.7%</span> from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Profit</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(17900)}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">+9.2%</span> margin improvement
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Revenue per Student</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(289)}</div>
            <p className="text-xs text-muted-foreground">
              Monthly average per student
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Retention Rate</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{performance?.retention || 89}%</div>
            <p className="text-xs text-muted-foreground">
              12-month retention rate
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Operational Insights Tabs */}
      <Tabs defaultValue="financial" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="financial">Financial</TabsTrigger>
          <TabsTrigger value="subscription">Subscription</TabsTrigger>
          <TabsTrigger value="growth">Growth Metrics</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="financial" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Revenue & Profit Trends</CardTitle>
                <CardDescription>
                  Monthly financial performance over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      name="Revenue"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="profit" 
                      stroke="#10B981" 
                      strokeWidth={2}
                      name="Profit"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Expense Breakdown</CardTitle>
                <CardDescription>
                  Current month expense distribution
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <RechartsPieChart>
                    <Pie
                      data={expenseBreakdown}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ category, percentage }) => `${category}: ${percentage}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="amount"
                    >
                      {expenseBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  </RechartsPieChart>
                </ResponsiveContainer>
                
                <div className="mt-4 space-y-2">
                  {expenseBreakdown.map((expense) => (
                    <div key={expense.category} className="flex justify-between text-sm">
                      <span>{expense.category}</span>
                      <span className="font-medium">{formatCurrency(expense.amount)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="subscription" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Current Subscription</CardTitle>
                <CardDescription>
                  Plan details and usage limits
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold text-lg">{subscriptionData.currentPlan} Plan</h3>
                      <p className="text-sm text-muted-foreground">
                        {formatCurrency(subscriptionData.billing.monthlyAmount)}/month
                      </p>
                    </div>
                    <Badge className="bg-green-500">
                      {subscriptionData.billing.status.toUpperCase()}
                    </Badge>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Players</span>
                        <span>
                          {subscriptionData.currentUsage.players} / {subscriptionData.planLimits.maxPlayers}
                        </span>
                      </div>
                      <Progress 
                        value={getUsagePercentage(
                          subscriptionData.currentUsage.players, 
                          subscriptionData.planLimits.maxPlayers
                        )} 
                      />
                    </div>

                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Coaches</span>
                        <span>
                          {subscriptionData.currentUsage.coaches} / {subscriptionData.planLimits.maxCoaches}
                        </span>
                      </div>
                      <Progress 
                        value={getUsagePercentage(
                          subscriptionData.currentUsage.coaches, 
                          subscriptionData.planLimits.maxCoaches
                        )} 
                      />
                    </div>

                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Monthly Sessions</span>
                        <span>
                          {subscriptionData.currentUsage.sessions} / {subscriptionData.planLimits.maxSessions}
                        </span>
                      </div>
                      <Progress 
                        value={getUsagePercentage(
                          subscriptionData.currentUsage.sessions, 
                          subscriptionData.planLimits.maxSessions
                        )} 
                      />
                    </div>

                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Storage</span>
                        <span>
                          {subscriptionData.currentUsage.storageGB} GB / {subscriptionData.planLimits.storageGB} GB
                        </span>
                      </div>
                      <Progress 
                        value={getUsagePercentage(
                          subscriptionData.currentUsage.storageGB, 
                          subscriptionData.planLimits.storageGB
                        )} 
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Billing Information</CardTitle>
                <CardDescription>
                  Payment details and billing history
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Payment Method</h4>
                    <div className="flex items-center space-x-2">
                      <CreditCard className="h-4 w-4" />
                      <span>{subscriptionData.billing.paymentMethod}</span>
                      <Badge variant="outline">Visa</Badge>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Next Billing</h4>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(subscriptionData.billing.nextBillingDate).toLocaleDateString()}</span>
                      <span className="text-sm text-muted-foreground">
                        ({formatCurrency(subscriptionData.billing.monthlyAmount)})
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Button className="w-full">
                      <Settings className="h-4 w-4 mr-2" />
                      Manage Subscription
                    </Button>
                    <Button variant="outline" className="w-full">
                      View Billing History
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="growth" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Growth Metrics</CardTitle>
                <CardDescription>
                  Key performance indicators and targets
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {growthMetrics.map((metric) => (
                    <div key={metric.metric} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{metric.metric}</span>
                        <div className="text-right">
                          <div className="text-sm font-semibold">
                            {metric.metric.includes('Revenue') ? 
                              formatCurrency(metric.current) : 
                              `${metric.current}${metric.metric.includes('Utilization') ? '%' : ''}`
                            }
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            +{metric.growth}%
                          </Badge>
                        </div>
                      </div>
                      <Progress 
                        value={(metric.current / metric.target) * 100} 
                        className="h-2"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Current: {
                          metric.metric.includes('Revenue') ? 
                            formatCurrency(metric.current) : 
                            `${metric.current}${metric.metric.includes('Utilization') ? '%' : ''}`
                        }</span>
                        <span>Target: {
                          metric.metric.includes('Revenue') ? 
                            formatCurrency(metric.target) : 
                            `${metric.target}${metric.metric.includes('Utilization') ? '%' : ''}`
                        }</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Student Retention Analysis</CardTitle>
                <CardDescription>
                  Retention rates by time period
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={retentionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="retained" fill="#10B981" name="Retained %" />
                    <Bar dataKey="churned" fill="#EF4444" name="Churned %" />
                  </BarChart>
                </ResponsiveContainer>
                
                <div className="mt-4 grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-green-600">89%</div>
                    <p className="text-sm text-muted-foreground">Annual Retention</p>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">4.2</div>
                    <p className="text-sm text-muted-foreground">Avg Years per Student</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Operational Efficiency</CardTitle>
                <CardDescription>
                  Key operational metrics and recommendations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold">Coach Efficiency</h4>
                      <Badge className="bg-green-500">Excellent</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      13 students per coach (industry avg: 10-12)
                    </p>
                    <Progress value={85} />
                  </div>

                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold">Facility Utilization</h4>
                      <Badge variant="secondary">Good</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      65% average utilization (target: 75%)
                    </p>
                    <Progress value={65} />
                  </div>

                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold">Customer Satisfaction</h4>
                      <Badge className="bg-green-500">Excellent</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      4.6/5 average rating (156 reviews)
                    </p>
                    <Progress value={92} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recommendations</CardTitle>
                <CardDescription>
                  AI-powered insights for operational improvement
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <TrendingUp className="h-5 w-5 text-green-500 mt-0.5" />
                      <div>
                        <h4 className="font-semibold">Increase Off-Peak Pricing</h4>
                        <p className="text-sm text-muted-foreground">
                          Implement dynamic pricing for 10 AM - 2 PM slots to increase facility utilization by 15%
                        </p>
                        <Badge variant="outline" className="mt-2">+$3,200/month potential</Badge>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <Users className="h-5 w-5 text-blue-500 mt-0.5" />
                      <div>
                        <h4 className="font-semibold">Expand Coaching Team</h4>
                        <p className="text-sm text-muted-foreground">
                          Add 2 part-time coaches to reduce wait times and improve service quality
                        </p>
                        <Badge variant="outline" className="mt-2">ROI: 240% in 6 months</Badge>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <Target className="h-5 w-5 text-purple-500 mt-0.5" />
                      <div>
                        <h4 className="font-semibold">Launch Group Programs</h4>
                        <p className="text-sm text-muted-foreground">
                          Introduce group training sessions to increase revenue per hour by 35%
                        </p>
                        <Badge variant="outline" className="mt-2">High Impact</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}